/** Confirm deleting the project by typing its name; then to `/` (another project), with a toast there. */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Dialog } from "@/components/ui/overlay";
import { errorText } from "@/components/ui/states";
import { useDeleteProject } from "@/features/projects/hooks";
import type { ProjectDetail } from "@/features/projects/types";
import { leaveProject } from "@/lib/project-url";
import { useUiStore } from "@/stores/ui-store";

export function DeleteProjectDialog({ project, onClose }: { project: ProjectDetail; onClose: () => void }) {
  const [typed, setTyped] = useState("");
  const remove = useDeleteProject();
  const notify = useUiStore((s) => s.notify);
  const ready = typed.trim() === project.name && !remove.isPending;
  const confirm = () =>
    remove.mutate(undefined, {
      // The project is gone: leave its router, forget it, and say so on the next page.
      onSuccess: () => leaveProject(project.slug, `Deleted ${project.name}.`),
      onError: (e) => notify(errorText(e, "Could not delete the project."), "error"),
    });
  return (
    <Dialog
      onSubmit={() => ready && confirm()}
      title={`Delete "${project.name}"?`}
      subtitle="Deletes the project and everything in it: datasets, evaluators, experiments and review queues. Running experiments stop."
      onClose={onClose}
      width={480}
      footer={
        <>
          <span className="flex-1" />
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={!ready}>
            Delete project
          </Button>
        </>
      }
    >
      <Field label={`Type “${project.name}” to confirm`} htmlFor="delete-project-confirm">
        <input
          id="delete-project-confirm"
          className="inp"
          autoComplete="off"
          autoFocus
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
        />
      </Field>
    </Dialog>
  );
}
